import { yupResolver } from '@hookform/resolvers/yup';
import { memo, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form';
import * as Yup from 'yup';

import { WorkspaceMemberRoleDisplayName, type WorkspaceUserRole } from '@air/api/types';
import { Badge } from '@air/primitive-badge';
import { Button } from '@air/primitive-button';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { Field, FieldLabel, FieldText } from '@air/primitive-field';
import { useAirModal } from '@air/provider-modal';

import { InviteEmailSelect } from '~/components/InviteEmailSelect/InviteEmailSelect';
import { DropdownMenuWithSelectedLabelButton } from '~/components/Menus/DropdownMenuWithSelectedLabelButton';
import { PlansModal } from '~/components/Modals/PlansModal/PlansModal';
import { usePlanFeature } from '~/hooks/usePlanFeature';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useCurrentWorkspaceMember } from '~/swr-hooks/members/useCurrentWorkspaceMember';
import { useWorkspaceMemberRoles } from '~/swr-hooks/members/useWorkspaceMemberRoles';
import { useReachedMemberLimit } from '~/swr-hooks/workspaces/useReachedMemberLimit';
import { MAX_INVITE_EMAILS } from '~/utils/InviteEmailUtils';
import { getLeastPermissiveRole, isPermissionsGTE } from '~/utils/PermissionsUtils';

export type InviteByEmailInputProps = {
  submitMembers: ({ emails, selectedRole }: { emails: string[]; selectedRole: WorkspaceUserRole }) => Promise<void>;
  onError: (error: unknown) => void;
};

const InviteByEmailFormValidationSchema = Yup.object()
  .shape({
    emails: Yup.array()
      .of(Yup.string().required())
      .min(1, 'Please enter a valid email address')
      .max(MAX_INVITE_EMAILS, `You can invite up to ${MAX_INVITE_EMAILS} people at a time.`)
      .required('Please enter a valid email address'),
    // Optional in the schema so `defaultValues` can omit it and the seeding effect can fill it in
    // once `leastPermissiveRole` resolves. The render guard at the bottom of the component proves
    // presence before the form is interactive, and the submit-disabled check requires it again.
    // Presence-only: role objects come from the curated workspace roles API, so structural shape is upstream.
    selectedRole: Yup.mixed<WorkspaceUserRole>().notRequired(),
  })
  .required();

// Two types because the schema's input and output shapes differ for `selectedRole`:
// - Input (mirrors `defaultValues` and the form's value store): required key, may be undefined
//   while the seeding effect runs.
// - Output (the validated payload that lands in `onSubmit`): optional key, present when
//   validation passes.
// RHF's third generic `TTransformedValues` separates these; `Yup.InferType` only gives us output.
type InviteByEmailFormInput = {
  emails: string[];
  selectedRole: WorkspaceUserRole | null | undefined;
};
type InviteByEmailFormOutput = Yup.InferType<typeof InviteByEmailFormValidationSchema>;

export const InviteByEmailInput = memo(({ submitMembers, onError }: InviteByEmailInputProps) => {
  const formId = useId();
  const [inviteEmailInputValue, setInviteEmailInputValue] = useState('');
  const { currentWorkspaceMember } = useCurrentWorkspaceMember();
  const { reachedMemberLimit } = useReachedMemberLimit();
  const { memberRoles } = useWorkspaceMemberRoles();
  const [showPlansModal] = useAirModal(PlansModal);
  const canHaveCommenters = usePlanFeature('commentOnlyMembers');
  const { data: subscription } = useSubscriptionContext();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  const filteredRoles = useMemo(() => {
    if (!memberRoles) {
      return undefined;
    }

    return memberRoles.filter((role) => role.enabled !== false);
  }, [memberRoles]);

  // Credit-based plans allow Commenters without upsell
  const allowCommenters = canHaveCommenters || subscription?.isCreditModel;

  const leastPermissiveRole = useMemo(() => {
    const roles = filteredRoles?.filter(
      (role) => allowCommenters || role.displayName !== WorkspaceMemberRoleDisplayName.Commenter,
    );

    return roles ? getLeastPermissiveRole(roles) : undefined;
  }, [allowCommenters, filteredRoles]);

  const {
    clearErrors,
    control,
    handleSubmit,
    setValue,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteByEmailFormInput, unknown, InviteByEmailFormOutput>({
    defaultValues: {
      emails: [],
      // selectedRole is set via the seeding effect below once leastPermissiveRole resolves.
      selectedRole: undefined,
    },
    resolver: yupResolver(InviteByEmailFormValidationSchema),
  });

  const emails = useWatch({ control, name: 'emails' });
  const selectedRole = useWatch({ control, name: 'selectedRole' });

  // Replace Formik's enableReinitialize: when the workspace's least-permissive role becomes
  // available (or changes), seed it as the default selectedRole if the user hasn't picked one yet.
  useEffect(() => {
    if (leastPermissiveRole && !selectedRole) {
      setValue('selectedRole', leastPermissiveRole);
    }
  }, [leastPermissiveRole, selectedRole, setValue]);

  const handleInviteEmailInputValueChange = useCallback((inputValue: string) => {
    setInviteEmailInputValue(inputValue);
  }, []);

  const handleEmailsChange = useCallback(
    (nextEmails: string[]) => {
      setValue('emails', nextEmails, { shouldDirty: true, shouldTouch: true, shouldValidate: false });
    },
    [setValue],
  );

  const handleEmailErrorChange = useCallback(
    (error: string | undefined) => {
      if (error) {
        setError('emails', { message: error });
      } else {
        clearErrors('emails');
      }
    },
    [clearErrors, setError],
  );

  const handleSelectRole = useCallback(
    (role: WorkspaceUserRole) => {
      setValue('selectedRole', role, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    },
    [setValue],
  );

  const onSubmit = useCallback<SubmitHandler<InviteByEmailFormOutput>>(
    async ({ emails: submittedEmails, selectedRole: submittedRole }) => {
      // The render guard below prevents this branch in practice (the form doesn't render until
      // `selectedRole` is set), but the schema models it as optional so `defaultValues` can omit
      // it during the brief mount → seeding-effect window.
      if (!submittedRole) {
        return;
      }

      try {
        await submitMembers({ emails: submittedEmails, selectedRole: submittedRole });
        setInviteEmailInputValue('');
        reset({
          emails: [],
          selectedRole: submittedRole,
        });
      } catch (error) {
        setError('emails', { message: 'Failed to add users. Please try again later.' });
        onError(error);
      }
    },
    [onError, reset, setError, submitMembers],
  );

  const roleOptions = useMemo<DropdownMenuOption[]>(
    () =>
      filteredRoles?.map((role) => {
        const { displayName, description, id } = role;

        if (!allowCommenters && displayName === WorkspaceMemberRoleDisplayName.Commenter) {
          return {
            id,
            onSelect: showPlansModal,
            label: (
              <span className="flex gap-1 overflow-visible whitespace-normal">
                {displayName}
                <Badge className="shrink-0" color="teal">
                  Upgrade
                </Badge>
              </span>
            ),
            description: description || undefined,
            type: 'item',
          };
        }

        return {
          id,
          onSelect: () => handleSelectRole(role),
          label: displayName,
          description: description || undefined,
          disabled: !workspacePermissions || !isPermissionsGTE(workspacePermissions, role.permissions),
          type: 'item',
        };
      }) || [],
    [allowCommenters, filteredRoles, handleSelectRole, showPlansModal, workspacePermissions],
  );

  if (
    !currentWorkspaceMember ||
    reachedMemberLimit === undefined ||
    !filteredRoles ||
    !workspacePermissions ||
    !leastPermissiveRole ||
    !selectedRole
  ) {
    return null;
  }

  const emailError = typeof errors.emails?.message === 'string' ? errors.emails.message : undefined;
  const isInviteDisabled =
    !emails?.length ||
    Boolean(emailError) ||
    Boolean(inviteEmailInputValue.trim()) ||
    !selectedRole ||
    reachedMemberLimit;
  const emailErrorId = `${formId}-emails-error`;
  const emailInputId = `${formId}-emails`;

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="flex items-end">
        <Field className="mt-4 mb-6 grow">
          <FieldLabel className="sr-only" htmlFor={emailInputId}>
            Email
          </FieldLabel>
          <InviteEmailSelect
            className="w-full"
            data-testid="INVITE_MEMBERS_EMAIL_SELECT"
            disabled={reachedMemberLimit}
            emails={emails ?? []}
            endAdornment={
              <DropdownMenuWithSelectedLabelButton
                selectedOption={{
                  id: selectedRole.id,
                  label: selectedRole.displayName,
                }}
                options={roleOptions}
                disabled={reachedMemberLimit}
              />
            }
            // Anchor the role dropdown to the top of the input so it stays at the top-right
            // regardless of how many chips wrap below it (the default vertical-centering
            // looks fine with 1-2 chips but drifts to the middle of the column with many).
            endAdornmentClassName="top-6"
            error={emailError}
            errorId={emailErrorId}
            id={emailInputId}
            onEmailsChange={handleEmailsChange}
            onErrorChange={handleEmailErrorChange}
            onInputValueChange={handleInviteEmailInputValueChange}
            placeholder="Email"
          />
          {!!emailError && (
            <FieldText color="red" id={emailErrorId} role="alert">
              {emailError}
            </FieldText>
          )}
        </Field>
        <Button
          appearance="filled"
          className="mb-6 ml-3 shrink-0"
          color="blue"
          disabled={isInviteDisabled}
          isLoading={isSubmitting}
          size="extra-large"
          type="submit"
        >
          Invite
        </Button>
      </div>
    </form>
  );
});

InviteByEmailInput.displayName = 'InviteByEmailInput';
